import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, Shield, Plus, Heart, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About Us" },
  { to: "/team", label: "Our Team" },
  { to: "/campaigns", label: "Our Services" },
  { to: "/campaigns", label: "Get Help" },
  { to: "/register", label: "Get Involved" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const canCreate = user && (user.role === "requester" || user.role === "admin");

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-bg/85 border-b border-brand">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-[84px] flex items-center justify-between">
        <Logo size="lg" />

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {links.map((l, i) => (
            <NavLink
              key={`${l.to}-${i}`}
              to={l.to}
              end={!!l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors py-1 ${
                  isActive ? "text-brand-primary" : "text-brand-text/80 hover:text-brand-primary"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-7 h-[3px] rounded-full bg-brand-primary" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            className="w-10 h-10 rounded-full bg-brand-pink hover:bg-brand-pink-hover flex items-center justify-center text-brand-wine transition-colors"
            title="Search"
            data-testid="navbar-search-btn"
            aria-label="Search"
          >
            <Search size={16} strokeWidth={2} />
          </button>
          {canCreate && (
            <Link to="/create" className="text-sm font-medium text-brand-wine hover:text-brand-primary" data-testid="navbar-start-campaign">
              <Plus size={14} strokeWidth={2} className="inline mr-1" />
              Start campaign
            </Link>
          )}
          {!user ? (
            <>
              <Link to="/login" className="text-sm font-medium text-brand-text hover:text-brand-primary transition-colors" data-testid="navbar-login-link">
                Sign in
              </Link>
              <Link to="/campaigns" className="btn-gold !py-2.5 !px-6 text-sm" data-testid="navbar-donate-now">
                <Heart size={14} strokeWidth={1.8} className="mr-1.5" fill="currentColor" />
                Donate Now
              </Link>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Link to="/admin" className="btn-pink !py-2 !px-3 text-sm" data-testid="navbar-admin-link">
                  <Shield size={15} strokeWidth={1.8} className="mr-1.5" />
                  Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-outline-gold !py-2 !px-4 text-sm" data-testid="navbar-dashboard-link">
                <LayoutDashboard size={15} strokeWidth={1.8} className="mr-1.5" />
                {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={handleLogout} className="text-brand-muted hover:text-brand-wine p-2 rounded-full transition-colors" title="Sign out" data-testid="navbar-logout-btn">
                <LogOut size={18} strokeWidth={1.6} />
              </button>
            </div>
          )}
        </div>

        <button className="lg:hidden p-2 text-brand-wine" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="navbar-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand bg-brand-surface">
          <div className="px-4 py-4 space-y-2">
            {links.map((l, i) => (
              <NavLink key={`m-${l.to}-${i}`} to={l.to} end={!!l.end} onClick={() => setOpen(false)}
                className="block py-2 text-brand-wine hover:text-brand-primary" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/ /g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-brand space-y-2">
              {canCreate && (
                <Link to="/create" onClick={() => setOpen(false)} className="btn-outline-gold w-full !py-2.5">Start campaign</Link>
              )}
              {!user ? (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-gold w-full !py-2.5">Sign in</Link>
                  <Link to="/campaigns" onClick={() => setOpen(false)} className="btn-gold w-full !py-2.5">
                    <Heart size={14} strokeWidth={1.8} className="mr-1.5" fill="currentColor" /> Donate Now
                  </Link>
                </>
              ) : (
                <>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="btn-pink w-full !py-2.5">Admin</Link>}
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-outline-gold w-full !py-2.5">Dashboard</Link>
                  <button onClick={() => { handleLogout(); setOpen(false); }} className="btn-pink w-full !py-2.5">
                    <LogOut size={15} className="mr-1.5" /> Sign out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
